import { ACCOUNT_TYPE, selectUserData, updateValue } from '../accountSlice'
import ContactChangeBlocked from 'components/ContactChangeBlocked'
import { parseApiErrors, pickFieldError } from 'rest/apiErrors'
import useContactChangeLock from 'hooks/useContactChangeLock'
import { DEFAULT_PREFIX, isValidPhone } from 'utils/phone'
import { useDispatch, useSelector } from 'react-redux'
import PhoneInput from 'components/inputs/PhoneInput'
import useTranslation from 'hooks/useTranslation'
import { useHistory } from 'react-router-dom'
import Submit from 'components/inputs/Submit'
import Input from 'components/inputs/Input'
import Popup from 'components/popups/Popup'
import { STATUS } from 'utils/constants'
import { POST } from 'rest/request'
import { user } from 'rest/urls'
import { useState } from 'react'

const type = ACCOUNT_TYPE.basic

const EditPhonePopup = ({ isAdditionalPhone = false }) => {

  const { data = {} } = useSelector(state => selectUserData(state, type))
  const [status, setStatus] = useState(STATUS.idle)
  const [warning, setWarning] = useState('')
  const [{ prefix, phone }, setPhone] = useState({ prefix: DEFAULT_PREFIX, phone: '' })
  const [password, setPassword] = useState('')
  const [passwordWarning, setPasswordWarning] = useState('')

  const { hasRentals } = useContactChangeLock()
  const isLocked = hasRentals && (!isAdditionalPhone || !!data?.additional_phone)

  const dispatch = useDispatch()
  const { goBack } = useHistory()
  const { t } = useTranslation('form')
  const updateVal = (name, value) => dispatch(updateValue({ type, name, value }))

  const submit = async () => {
    const body = isAdditionalPhone
      ? { additional_prefix: prefix, additional_phone: phone }
      : { prefix, phone }
    setStatus(STATUS.pending)

    try {
      const response = await POST(`${user}/phone`, { body: { ...body, password } }, false)
      if (response) {
        updateVal('data', { ...data, ...body })
        goBack()
      } else {
        throw new Error('Wrong response')
      }

    } catch (error) {
      setStatus(STATUS.failed)
      const { fields } = parseApiErrors(error)
      const contactError = pickFieldError(fields, ['phone', 'prefix', 'additional_phone', 'additional_prefix'])
      const passwordError = pickFieldError(fields, ['password', 'current_password'])
      if (contactError) setWarning(contactError)
      if (passwordError) setPasswordWarning(passwordError)
    }
  }

  const validatePhone = () => {
    const isPhoneValid = isValidPhone(prefix, phone)
    if (!isPhoneValid) setWarning(t(phone ? 'wrongNumber' : 'enterPhoneNumber'))
    if (!password) setPasswordWarning(t('enterPassword'))
    if (isPhoneValid && password) submit()
  }

  return (
    <Popup>
      <div className='popup__verification'>
        <div className='popup__title'>
          <div>{t(isAdditionalPhone ? 'changeAdditionalPhoneNumber' : 'changePhoneNumber')}</div>
        </div>

        {isLocked
          ? <ContactChangeBlocked />
          : <div className='popup__form'>
            <PhoneInput
              required
              name='phone'
              value={phone}
              prefix={prefix}
              warning={warning}
              label={t('phoneNumber')}
              placeholder={t('enterPhoneNumber')}
              disabled={status === STATUS.pending}
              blurHandler={() => setWarning(!phone || isValidPhone(prefix, phone) ? false : t('wrongNumber'))}
              setValue={value => {
                setPhone(value)
                if (warning && isValidPhone(value.prefix, value.phone)) setWarning(false)
              }} />

            <Input
              required
              name='password'
              type='password'
              autoComplete='current-password'
              value={password}
              label={t('currentPassword')}
              minLength='6'
              maxLength='90'
              warning={passwordWarning}
              placeholder={t('enterPassword')}
              disabled={status === STATUS.pending}
              changeHandler={e => {
                setPassword(e.target.value)
                if (passwordWarning && e.target.value) setPasswordWarning('')
              }} />

            <Submit
              text={t((isAdditionalPhone ? data?.additional_phone : data?.phone) ? 'change' : 'add')}
              block={status === STATUS.pending}
              isLoading={status === STATUS.pending}
              action={validatePhone} />
          </div>}
      </div>
    </Popup>
  )
}

export default EditPhonePopup